import React, { CSSProperties, useMemo, useRef } from 'react'
import { useFloating, offset, flip, shift, arrow, autoUpdate } from '@floating-ui/react-dom'
import type { Placement } from '@floating-ui/react-dom'

import type {
  PopoverAlign,
  PopoverFloatingProps,
  PopoverPosition,
  PopoverProps,
  PopoverReferenceProps,
  UsePopoverReturn,
} from './Popover.types'

const PopoverContext = React.createContext<UsePopoverReturn | undefined>(undefined)

export function usePopover(): UsePopoverReturn {
  const value = React.useContext(PopoverContext)

  if (!value) {
    throw new Error('usePopover must be used within a <Popover> Provider')
  }

  return value
}

function Popover(props: PopoverProps): JSX.Element {
  const arrowRef = useRef<HTMLElement | null>(null)

  const { position = 'bottom', align = 'start', strategy = 'fixed' } = props

  const desiredPlacement: Placement = `${position}${
    align === 'center' ? '' : `-${align}`
  }` as Placement

  const floating = useFloating({
    placement: desiredPlacement,
    strategy,
    middleware: [offset(10), flip(), shift(), arrow({ element: arrowRef, padding: 8 })],
    // TODO: FloatingUI docs states that `autoUpdate` is expensive.
    // This should be properly investidated since it's the way to
    // update fixed positions after scrolling.
    whileElementsMounted: autoUpdate,
  })

  const [resultPosition = position, resultAlign = align] = floating.placement.split('-') as [
    PopoverPosition,
    PopoverAlign,
  ]

  const value = useMemo<UsePopoverReturn>(
    () => ({
      strategy,
      register: {
        setReference: floating.refs.setReference,
        setFloating: floating.refs.setFloating,
        setArrow: arrowRef,
      },
      result: {
        floatingStyles: floating.floatingStyles,
        arrow: { top: floating.middlewareData.arrow?.y, left: floating.middlewareData.arrow?.x },
        position: resultPosition,
        align: resultAlign,
      },
    }),
    [
      floating.refs.setFloating,
      floating.middlewareData.arrow?.x,
      floating.middlewareData.arrow?.y,
      floating.refs.setReference,
      floating.floatingStyles,
      resultAlign,
      resultPosition,
      strategy,
    ]
  )

  return <PopoverContext.Provider value={value}>{props.children}</PopoverContext.Provider>
}

function PopoverReference({ children, ...others }: PopoverReferenceProps): JSX.Element {
  const { register } = usePopover()

  return (
    <div ref={register.setReference} {...others}>
      {children}
    </div>
  )
}

function PopoverFloating({ children, style, ...others }: PopoverFloatingProps): JSX.Element {
  const { register, result } = usePopover()

  return (
    <div
      ref={register.setFloating}
      {...others}
      style={{
        ...result.floatingStyles,
        ...style,
      }}
    >
      {children}
    </div>
  )
}

Popover.Floating = PopoverFloating
Popover.Reference = PopoverReference

export default Popover
